import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import './AttendanceSummary.css';

const AttendanceSummary = ({ onBack, selectedClass, selectedDate }) => {
  const [attendanceData, setAttendanceData] = useState({
    regularAttendance: [],
    ecaAttendance: [],
    slpAttendance: [],
    summary: {
      totalStudents: 0,
      present: 0,
      absent: 0,
      late: 0,
      early_leave: 0,
      out: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('regular'); // 'regular', 'eca', or 'slp'
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (selectedClass && selectedDate) {
      fetchAttendanceSummary();
    }
  }, [selectedClass, selectedDate]);

  const fetchAttendanceSummary = async () => {
    try {
      setLoading(true);
      console.log('Fetching attendance summary for:', { selectedClass, selectedDate });
      
      // Fetch regular class attendance records
      const { data: regularRecords, error: regularError } = await supabase
        .from('attendance_records')
        .select('*')
        .eq('tenant_id', 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3')
        .eq('class_id', selectedClass)
        .eq('attendance_date', selectedDate);

      if (regularError) {
        console.error('Error fetching regular attendance:', regularError);
      } else {
        console.log('Regular attendance records:', regularRecords?.length || 0);
      }

      // Fetch ECA attendance records for students in the selected class
      const { data: ecaRecords, error: ecaError } = await supabase
        .from('eca_attendance_records')
        .select(`
          *,
          eca_activities(name, name_en),
          users(id)
        `)
        .eq('date', selectedDate);

      if (ecaError) {
        console.error('Error fetching ECA attendance:', ecaError);
      }

      // Fetch SLP attendance records for students in the selected class
      const { data: slpRecords, error: slpError } = await supabase
        .from('slp_attendance_records')
        .select(`
          *,
          slp_activities(title, title_en)
        `)
        .eq('date', selectedDate);

      if (slpError) {
        console.error('Error fetching SLP attendance:', slpError);
      }

      // Get students in the selected class to filter ECA and SLP records
      const { data: classStudents, error: studentsError } = await supabase
        .from('saas_students')
        .select('student_id, name, student_code, class_display, class_no')
        .eq('tenant_id', 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3')
        .eq('class_id', selectedClass);

      if (studentsError) {
        console.error('Error fetching class students:', studentsError);
      } else {
        console.log('Class students found:', classStudents?.length || 0);
        console.log('Selected class:', selectedClass);
        console.log('Selected date:', selectedDate);
      }

      // Process and combine data
      const processedData = processAttendanceData(
        regularRecords || [], 
        ecaRecords || [], 
        slpRecords || [],
        classStudents || []
      );
      setAttendanceData(processedData);
      
    } catch (error) {
      console.error('Error in fetchAttendanceSummary:', error);
    } finally {
      setLoading(false);
    }
  };

  const processAttendanceData = (regularRecords, ecaRecords, slpRecords, classStudents) => {
    console.log('Processing attendance data:', {
      regularRecords: regularRecords?.length || 0,
      ecaRecords: ecaRecords?.length || 0, 
      slpRecords: slpRecords?.length || 0,
      classStudents: classStudents?.length || 0
    });
    
    // Create a map of class students for filtering
    const classStudentMap = new Map(classStudents.map(s => [s.student_id, s]));
    console.log('Class student map created:', classStudentMap.size, 'students');
    console.log('Sample student IDs in map:', Array.from(classStudentMap.keys()).slice(0, 3));

    // Process regular class attendance
    const regularAttendance = regularRecords.map(record => {
      const student = classStudentMap.get(record.student_id);
      return {
        id: record.id,
        studentId: record.student_id,
        studentName: student?.name || 'Unknown',
        studentCode: student?.class_display && student?.class_no ? 
          `${student.class_display}${student.class_no.toString().padStart(2, '0')}` : 
          (student?.student_code || 'N/A'),
        status: record.attendance_status,
        time: record.created_at ? new Date(record.created_at).toLocaleTimeString() : 'N/A',
        notes: record.notes || '',
        type: 'regular',
        activityName: '日常課堂'
      };
    });

    // Process ECA attendance - filter for students in the selected class
    const ecaAttendance = ecaRecords
      .filter(record => classStudentMap.has(record.student_id))
      .map(record => {
        const student = classStudentMap.get(record.student_id);
        return {
          id: record.id,
          studentId: record.student_id,
          studentName: student?.name || 'Unknown',
          studentCode: student?.class_display && student?.class_no ? 
            `${student.class_display}${student.class_no.toString().padStart(2, '0')}` : 
            (student?.student_code || 'N/A'),
          status: record.status,
          time: record.arrival_time || 'N/A',
          notes: record.notes || '',
          type: 'eca',
          activityName: record.eca_activities?.name || 'ECA活動'
        };
      });

    // Process SLP attendance - filter for students in the selected class
    const slpAttendance = slpRecords
      .filter(record => classStudentMap.has(record.student_id))
      .map(record => {
        const student = classStudentMap.get(record.student_id);
        return {
          id: record.id,
          studentId: record.student_id,
          studentName: student?.name || 'Unknown',
          studentCode: student?.class_display && student?.class_no ? 
            `${student.class_display}${student.class_no.toString().padStart(2, '0')}` : 
            (student?.student_code || 'N/A'),
          status: record.status,
          time: record.arrival_time || 'N/A',
          notes: record.notes || '',
          type: 'slp',
          activityName: record.slp_activities?.title || 'SLP活動'
        };
      });

    // Calculate summary statistics from all records
    const allRecords = [...regularAttendance, ...ecaAttendance, ...slpAttendance];
    const summary = {
      totalStudents: allRecords.length,
      present: allRecords.filter(r => r.status === 'present').length,
      absent: allRecords.filter(r => r.status === 'absent').length,
      late: allRecords.filter(r => r.status === 'late').length,
      early_leave: allRecords.filter(r => r.status === 'early_leave').length,
      out: allRecords.filter(r => r.status === 'out').length
    };

    return {
      regularAttendance,
      ecaAttendance,
      slpAttendance,
      summary
    };
  };

  const getStatusColor = (status) => {
    const colors = {
      present: '#28a745',
      absent: '#dc3545',
      late: '#fd7e14',
      early_leave: '#6f42c1',
      out: '#17a2b8'
    };
    return colors[status] || '#6c757d';
  };

  const getStatusText = (status) => {
    const texts = {
      present: '出席',
      absent: '缺席',
      late: '遲到',
      early_leave: '早退',
      out: '外出'
    };
    return texts[status] || status;
  };

  const getFilteredRecords = () => {
    let records;
    switch (viewMode) {
      case 'regular':
        records = attendanceData.regularAttendance;
        break;
      case 'eca':
        records = attendanceData.ecaAttendance;
        break;
      case 'slp':
        records = attendanceData.slpAttendance;
        break;
      default:
        records = attendanceData.regularAttendance;
    }
    
    if (filterStatus === 'all') {
      return records;
    }
    
    return records.filter(record => record.status === filterStatus);
  };

  const exportToCSV = () => {
    const records = getFilteredRecords();
    const csvContent = [
      ['學生資訊', '出勤狀態', '時間', '備註', '類型'],
      ...records.map(record => [
        `${record.studentCode} ${record.studentName}`,
        getStatusText(record.status),
        record.time,
        record.notes,
        record.type === 'eca' ? `ECA: ${record.activityName}` : 
        record.type === 'slp' ? `SLP: ${record.activityName}` : '正規課程'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `出勤記錄_${selectedClass}_${selectedDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="attendance-summary-page">
        <div className="loading">載入中...</div>
      </div>
    );
  }

  const filteredRecords = getFilteredRecords();

  return (
    <div className="attendance-summary-page">
      {/* Header */}
      <div className="summary-header">
        <button className="back-button" onClick={onBack}>
          ← 返回
        </button>
        <h1 className="summary-title">出勤記錄摘要</h1>
        <div className="summary-info">
          <div className="class-name">{selectedClass}</div>
          <div className="summary-date">{selectedDate}</div>
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="summary-stats">
        <div className="stat-card total">
          <div className="stat-number">{attendanceData.summary.totalStudents}</div>
          <div className="stat-label">總學生數</div>
        </div>
        <div className="stat-card present">
          <div className="stat-number">{attendanceData.summary.present}</div>
          <div className="stat-label">出席</div>
        </div>
        <div className="stat-card absent">
          <div className="stat-number">{attendanceData.summary.absent}</div>
          <div className="stat-label">缺席</div>
        </div>
        <div className="stat-card late">
          <div className="stat-number">{attendanceData.summary.late}</div>
          <div className="stat-label">遲到</div>
        </div>
        <div className="stat-card early-leave">
          <div className="stat-number">{attendanceData.summary.early_leave}</div>
          <div className="stat-label">早退</div>
        </div>
        <div className="stat-card out">
          <div className="stat-number">{attendanceData.summary.out}</div>
          <div className="stat-label">外出</div>
        </div>
      </div>

      {/* View Controls */}
      <div className="view-controls">
        <div className="view-tabs">
          <button
            className={`view-tab ${viewMode === 'regular' ? 'active' : ''}`}
            onClick={() => setViewMode('regular')}
          >
            日常課堂出勤 ({attendanceData.regularAttendance.length})
          </button>
          <button
            className={`view-tab ${viewMode === 'eca' ? 'active' : ''}`}
            onClick={() => setViewMode('eca')}
          >
            ECA 課外活動 ({attendanceData.ecaAttendance.length})
          </button>
          <button
            className={`view-tab ${viewMode === 'slp' ? 'active' : ''}`}
            onClick={() => setViewMode('slp')}
          >
            SLP 學習經歷 ({attendanceData.slpAttendance.length})
          </button>
        </div>
        
        <div className="filter-controls">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="status-filter"
          >
            <option value="all">全部狀態</option>
            <option value="present">出席</option>
            <option value="absent">缺席</option>
            <option value="late">遲到</option>
            <option value="early_leave">早退</option>
            <option value="out">外出</option>
          </select>
          
          <button className="export-button" onClick={exportToCSV}>
            📊 匯出 CSV
          </button>
        </div>
      </div>

      {/* Attendance Records Table */}
      <div className="records-table-container">
        <table className="records-table">
          <thead>
            <tr>
              <th>學生資訊</th>
              <th>出勤狀態</th>
              <th>時間</th>
              <th>備註</th>
              <th>活動名稱</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((record) => (
              <tr key={`${record.type}-${record.id}`}>
                <td className="student-info">
                  <div className="student-id-name">
                    <span className="student-id">{record.studentCode}</span>
                    <span className="student-name">{record.studentName}</span>
                  </div>
                </td>
                <td>
                  <span 
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(record.status) }}
                  >
                    {getStatusText(record.status)}
                  </span>
                </td>
                <td className="attendance-time">{record.time}</td>
                <td className="attendance-notes">{record.notes}</td>
                <td className="attendance-type">
                  <span className={`type-badge ${record.type}`}>
                    {record.activityName}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredRecords.length === 0 && (
          <div className="no-records">
            <p>沒有找到符合條件的出勤記錄</p>
          </div>
        )}
      </div>

      {/* Summary Footer */}
      <div className="summary-footer">
        <div className="footer-stats">
          <span>顯示記錄: {filteredRecords.length} 筆</span>
          <span>總記錄: {attendanceData.summary.totalStudents} 筆</span>
        </div>
        <div className="footer-actions">
          <button className="print-button" onClick={() => window.print()}>
            🖨️ 列印
          </button>
        </div>
      </div>
    </div>
  );
};

export default AttendanceSummary;
